import type { ApiClient } from '../client'
import type {
  PublicPlansResponse,
  PublicAvailability,
  PublicAvailabilityQuery,
  PublicBookingRequest,
  PublicBookingResult,
  PublicOrganization,
  PublicWaitlistRequest,
  PublicWaitlistResult,
} from '../types'

/**
 * The public tenant API (RecoupSlots P11) — src/modules/public-api/controllers/public-orgs.controller.ts.
 * Unauthenticated: every call is `auth: false`, so a staff session in the same browser is never sent
 * and never refreshed. Submissions are NOT retried automatically — the server counts every attempt
 * against D9's per-phone and per-IP limits, and a booking must not be posted twice by the client.
 */
const NO_RETRY = {
  rateLimited: { attempts: 0, maxWaitMs: 0 },
  capacity: { attempts: 0, baseDelayMs: 0 },
  network: { attempts: 0, delayMs: 0 },
}

export function publicEndpoints(api: ApiClient) {
  const path = (slug: string, suffix = '') => `public/orgs/${encodeURIComponent(slug)}${suffix}`
  return {
    organization: (slug: string) => api.get<PublicOrganization>(path(slug), { auth: false }),
    /** The pricing page's catalog (P8). Static; no session, no tenant. */
    plans: () => api.get<PublicPlansResponse>('public/plans', { auth: false }),
    availability: (slug: string, query: PublicAvailabilityQuery) =>
      api.get<PublicAvailability>(path(slug, '/availability'), {
        auth: false,
        query: { ...query },
      }),
    book: (slug: string, body: PublicBookingRequest) =>
      api.post<PublicBookingResult>(path(slug, '/bookings'), body, {
        auth: false,
        retry: NO_RETRY,
      }),
    joinWaitlist: (slug: string, body: PublicWaitlistRequest) =>
      api.post<PublicWaitlistResult>(path(slug, '/waitlist'), body, {
        auth: false,
        retry: NO_RETRY,
      }),
  }
}
